import { inject, Injectable } from '@angular/core';
import { UserService } from '../services/user.service';
import { Resolve } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiProxy } from '@core/proxies/api.proxy';
import { environment } from '@environments/environment';
import { SettingsStore, NominationSettings } from '@core/stores/settings.store';
import { UserWithRelations } from '@core/types/user.types';
import { GlobalSettingType } from '@core/types/base.types';

@Injectable({ providedIn: 'root' })
export class UserAndSettingsResolver implements Resolve<Promise<ResolverResult>> {
	proxy = new ApiProxy(this.http);
	readonly userService = inject(UserService);
	readonly settingsStore = inject(SettingsStore);

	constructor(
		protected http: HttpClient,
		private msalService: MsalService
	) {}

	async resolve(): Promise<ResolverResult> {
		let account = this.msalService.instance.getActiveAccount();
		if (!account) {
			try {
				const result = await this.msalService.instance.acquireTokenSilent({
					scopes: environment.msalConfig.scopes.custom
				});
				account = result.account;
				this.msalService.instance.setActiveAccount(account);
			} catch {
				this.msalService.loginRedirect();
			}
		}

		const [user, nominationSettings] = await Promise.all([
			firstValueFrom(this.proxy.user.GetUser()),
			firstValueFrom(this.proxy.GetSettings<NominationSettings>(GlobalSettingType.Nomination))
		]);

		if (user) this.userService.setVoltaUser(user);
		this.settingsStore.setNotification({
			...(user.Setting?.Parameters && user.Setting?.Parameters),
			Rules: user.NotificationRules,
			Unsubscriptions: user.NotificationUnsubscriptions.map(m => m.Id)
		});

		if (nominationSettings) this.settingsStore.setNomination(nominationSettings.Parameters);
		return { user, nominationSettings: nominationSettings.Parameters };
	}
}

export type ResolverResult = {
	user: UserWithRelations;
	nominationSettings: NominationSettings;
};
